import type {
  Qualification,
  Question,
  QuestionScoring,
  QuestionType,
  ScoringMode,
} from "../types";

export interface QuestionScoreResult {
  question_id: string;
  question_label: string;
  /** The section the question is shown under (`Question.group`). */
  question_group?: string;
  question_type: QuestionType;
  /** A fraction, 0 to 1. `null` when the question does not count (`default: "ignore"`). */
  score: number | null;
  /**
   * The score is not known here: a text question is scored by an LLM on the
   * server, and a submission scored before the server stored its breakdown
   * carries none. A recalculation fills it in.
   */
  unknown?: boolean;
  mode: ScoringMode;
  weight: number;
}

/**
 * Every score is a fraction, 0 to 1. Forms generated before that was enforced
 * wrote 0–100: a value above 1 is read as a percentage, as the server does
 * (`toFraction`, `qualifications/services/internal/scoring.ts`).
 */
export function toFraction(value: number): number {
  if (typeof value !== "number" || isNaN(value)) return 0;
  const fraction = value > 1 ? value / 100 : value;
  return Math.max(0, Math.min(1, fraction));
}

/**
 * Each question's score for a submission: what the server stored when it scored
 * it, else worked out here from the snapshot (text questions left unknown).
 */
export function questionScoresOf(
  qualification: Qualification,
): QuestionScoreResult[] {
  const questions = qualification.form_snapshot?.questions || [];
  const stored = qualification.score_details?.questions;
  if (!stored?.length) {
    return calculateQuestionScores(questions, qualification.responses || {});
  }

  const byId = new Map(stored.map((qs) => [qs.question_id, qs]));
  return questions.map((question) => {
    const qs = byId.get(question.id);
    return {
      question_id: question.id,
      question_label: question.label,
      question_group: question.group,
      question_type: question.type,
      score: qs ? qs.score : null,
      unknown: !qs,
      mode: question.scoring_mode,
      weight: question.scoring.weight ?? 1,
    };
  });
}

/**
 * How the final score is made: min(minimum questions, weighted average of the
 * others) — `computeFinalScore` on the server. `null` parts are absent, and the
 * whole is `null` when a score it depends on is unknown.
 */
export function scoreBreakdown(scores: QuestionScoreResult[]): {
  minimum: number | null;
  weighted: number | null;
} | null {
  if (scores.some((qs) => qs.unknown)) return null;
  const counted = scores.filter(
    (qs): qs is QuestionScoreResult & { score: number } => qs.score !== null,
  );

  const minimums = counted.filter((qs) => qs.mode === "minimum");
  const weighted = counted.filter((qs) => qs.mode === "weight");
  const totalWeight = weighted.reduce((sum, qs) => sum + qs.weight, 0);

  return {
    minimum: minimums.length
      ? Math.min(...minimums.map((qs) => qs.score))
      : null,
    weighted:
      weighted.length && totalWeight > 0
        ? weighted.reduce((sum, qs) => sum + qs.score * qs.weight, 0) /
          totalWeight
        : null,
  };
}

/**
 * Score a single question based on its type and configuration — the server's
 * rules, for a submission it scored before storing each question's score.
 */
export function scoreQuestion(
  question: Question,
  answer: unknown,
): number | "ignore" | "unknown" {
  const { scoring } = question;

  // Collected, never scored
  if (question.scoring_mode === "none") return "ignore";

  // Handle missing answer
  if (answer === undefined || answer === null || answer === "") {
    return scoring.default === "ignore"
      ? "ignore"
      : toFraction(scoring.default);
  }

  switch (question.type) {
    case "boolean":
      return toFraction(scoreBooleanQuestion(answer, scoring));
    case "select":
      return toFraction(scoring.values?.[String(answer)] ?? 0);
    case "number":
      return toFraction(scoreNumberQuestion(answer, scoring));
    case "text":
      // Scored by an LLM on the server
      return "unknown";
    default:
      return 0;
  }
}

/** The keys a boolean answer may be scored under ("yes"/"no" from older forms). */
const BOOLEAN_KEYS: Record<string, string[]> = {
  true: ["true", "yes", "oui"],
  false: ["false", "no", "non"],
};

function scoreBooleanQuestion(
  answer: unknown,
  scoring: QuestionScoring,
): number {
  const byKey = new Map(
    Object.entries(scoring.values || {}).map(([key, value]) => [
      key.trim().toLowerCase(),
      value,
    ]),
  );
  for (const key of BOOLEAN_KEYS[String(answer)] ?? [String(answer)]) {
    const value = byKey.get(key);
    if (value !== undefined) return value;
  }
  return 0;
}

function scoreNumberQuestion(
  answer: unknown,
  scoring: QuestionScoring,
): number {
  const value = Number(answer);
  for (const range of scoring.ranges || []) {
    if (matchesOperator(value, range.operator, range.threshold)) {
      return range.value;
    }
  }
  return 0;
}

function matchesOperator(
  value: number,
  operator: "gte" | "lte" | "gt" | "lt" | "eq",
  threshold: number,
): boolean {
  switch (operator) {
    case "gte":
      return value >= threshold;
    case "lte":
      return value <= threshold;
    case "gt":
      return value > threshold;
    case "lt":
      return value < threshold;
    case "eq":
      return value === threshold;
    default:
      return false;
  }
}

/**
 * Calculate scores for all questions
 */
export function calculateQuestionScores(
  questions: Question[],
  responses: Record<string, unknown>,
): QuestionScoreResult[] {
  return questions.map((question) => {
    const score = scoreQuestion(question, responses[question.id]);
    return {
      question_id: question.id,
      question_label: question.label,
      question_group: question.group,
      question_type: question.type,
      score: typeof score === "number" ? score : null,
      unknown: score === "unknown",
      mode: question.scoring_mode,
      weight: question.scoring.weight ?? 1,
    };
  });
}

/**
 * A submission is created at 0 while the call is on, and only scored when the
 * conversation is wrapped up: a 0 with no per-question details is "not scored
 * yet", not a poor score.
 */
export const isAwaitingScore = (q: {
  score?: number | null;
  score_details?: unknown;
}) => !q.score && !q.score_details;

/** The same three bands as the submissions' filters: good, to look at, poor. */
export const scoreTone = (score: number) =>
  score >= 0.8
    ? {
        text: "text-success-foreground",
        dot: "bg-success",
        soft: "bg-success-soft",
      }
    : score < 0.5
      ? {
          text: "text-danger-foreground",
          dot: "bg-danger",
          soft: "bg-danger-soft",
        }
      : {
          text: "text-warning-foreground",
          dot: "bg-warning",
          soft: "bg-warning-soft",
        };
